<script setup lang="ts">
import type { DayOffGroup } from '@/composables/useDayOff';
import { useDayOff, type DayOffExclusionOptions } from '@/composables/useDayOff';
import type { User } from '@/types/api';
import Button from 'primevue/button';
import Skeleton from 'primevue/skeleton';
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import DayOffCard from './DayOffCard.vue';
import DayOffSelection from './DayOffSelection.vue';

const props = defineProps<{
  currentUser: User | null;
  loading: boolean;
  edit?: boolean;
  // Exclusion configuration
  excludeWeekends?: boolean;
  excludeDaysOfWeek?: number[];
  excludeDates?: Date[];
}>();

const { t } = useI18n();

// Use the day off composable with the current user
const currentUserRef = computed(() => props.currentUser);
const {
  loadingDaysOff,
  groupedDaysOff,
  remainingDaysOff,
  shouldShowLimitInfo,
  generateDisabledDatesForCalendar,
  generateDisabledDaysOfWeek,
  fetchUserDaysOff,
  addDaysOff,
  confirmDeleteDaysOff,
} = useDayOff(currentUserRef);

const isSidebarOpen = ref(false);

// Create exclusion options from props
const exclusionOptions = computed(
  (): DayOffExclusionOptions => ({
    excludeWeekends: props.excludeWeekends || false,
    excludeDaysOfWeek: props.excludeDaysOfWeek || [],
    excludeDates: props.excludeDates || [],
  }),
);

// Generate disabled dates and days of week for calendar
const disabledDates = computed(() => generateDisabledDatesForCalendar(exclusionOptions.value));

const disabledDaysOfWeek = computed(() => generateDisabledDaysOfWeek(exclusionOptions.value));

// Watch for user changes and load data
watchEffect(() => {
  if (props.currentUser?.id && props.currentUser.active_restaurant?.id) {
    fetchUserDaysOff();
  }
});

async function handleAddDaysOff(dates: Date[]) {
  const result = await addDaysOff(dates);
  if (result.success) {
    isSidebarOpen.value = false;
  }
}

function handleDeleteDaysOff(group: DayOffGroup) {
  confirmDeleteDaysOff(group, () => {
    // Optional callback after deletion
  });
}

function handleOpenSidebar() {
  console.log('Opening sidebar, current state:', isSidebarOpen.value);
  isSidebarOpen.value = true;
  console.log('Sidebar state after setting to true:', isSidebarOpen.value);
}

onMounted(() => {
  if (props.currentUser?.id && props.currentUser.active_restaurant?.id) {
    fetchUserDaysOff();
  }
});
</script>

<template>
  <div v-if="currentUser">
    <!-- FEATURE FLAG: Only show limit info if backend provides it -->
    <p v-if="shouldShowLimitInfo" class="flex flex-row justify-between">
      {{ t('dayOff.available') }} :
      <Skeleton v-if="loading || loadingDaysOff" width="6rem" height="1rem" />
      <span v-else class="font-bold">{{ remainingDaysOff }} {{ t('dayOff.days') }}</span>
    </p>
    <div class="mt-2 flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <div v-if="loading || loadingDaysOff">
          <div class="border-round border-1 surface-border surface-card align-center mb-2 flex flex-row items-center justify-between p-4">
            <Skeleton width="15rem" height="2rem"></Skeleton>
            <Skeleton width="3rem" height="3rem"></Skeleton>
          </div>
        </div>
        <div v-else-if="groupedDaysOff.length > 0" class="flex flex-col gap-2">
          <DayOffCard
            v-for="group in groupedDaysOff"
            :key="group.id"
            :group="group"
            :show-status="true"
            :show-delete-button="group.status.value === 'pending'"
            @delete="handleDeleteDaysOff"
          />
        </div>
        <p v-else-if="!loading && !loadingDaysOff && groupedDaysOff.length === 0">
          {{ t('dayOff.noUpcomingDaysOff') }}
        </p>
        <Button
          icon="pi pi-plus"
          class="mt-2 w-full"
          :label="t('dayOff.addDayOffButton')"
          :disabled="loading || loadingDaysOff"
          @click="handleOpenSidebar"
        />
      </div>
    </div>

    <!-- Use the new DayOffSelection component -->
    <DayOffSelection
      v-model="isSidebarOpen"
      :exclusion-options="exclusionOptions"
      :disabled-dates="disabledDates"
      :disabled-days-of-week="disabledDaysOfWeek"
      :remaining-days-off="remainingDaysOff"
      :should-show-limit-info="shouldShowLimitInfo"
      @add-days-off="handleAddDaysOff"
    />
  </div>
  <div v-else-if="loading">
    <Skeleton width="100%" height="10rem"></Skeleton>
  </div>
  <div v-else>
    <p>{{ t('dayOff.noUserData') }}</p>
  </div>
</template>

<style scoped>
/* Styles moved to component-specific files */
</style>
